/**
 * En-tête de page : le seul `<h1>` de l'écran, ce qui le situe (sur-titre,
 * statut) et ses actions. Une seule action principale (`primary`) ; une
 * action secondaire éventuelle ; le reste dans le menu « … ». Le retour est
 * visible au téléphone et sur tablette tactile, masqué à la souris (décision
 * du 12/10/2026 : colonne latérale et retour du navigateur suffisent). Une
 * fois passé sous la barre du haut, l'en-tête y laisse son titre et son retour.
 */
import { createContext, useContext, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { t } from '../../core/i18n.js';
import { ActionMenu, type ActionMenuItem } from '../action-menu.js';
import { usePageChrome } from '../shell/chrome.js';
import { ActionButton, type Action } from './action.js';

/** Niveau de titre des sections : 2 sous l'en-tête, +1 par section imbriquée. */
export const HeadingLevel = createContext(2);

export function useHeadingLevel(): number {
  return useContext(HeadingLevel);
}

/**
 * Phrase d'usage de l'écran. Au téléphone d'une liste elle tient sur une ligne
 * (le premier enregistrement doit rester dans le premier écran) et se déplie
 * d'un toucher quand elle est plus longue ; ailleurs elle s'affiche entière.
 */
function PageDescription(props: { children: ReactNode; compact: boolean }): ReactNode {
  const text = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [clipped, setClipped] = useState(false);
  useEffect(() => {
    const node = text.current;
    if (!node || !props.compact) return undefined;
    const measure = (): void => {
      // Mesuré replié seulement : une fois déplié, le bouton « Réduire » reste.
      if (!node.parentElement?.classList.contains('page-header__description--open')) {
        setClipped(node.scrollHeight > node.clientHeight + 1);
      }
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(node);
    return () => observer?.disconnect();
  }, [props.compact, props.children]);
  if (!props.compact) return <p className="page-header__description">{props.children}</p>;
  const toggle = clipped || open;
  const flip = (): void => setOpen((value) => !value);
  return (
    <p
      className={`page-header__description page-header__description--compact${open ? ' page-header__description--open' : ''}`}
      {...(toggle
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-expanded': open,
            title: open ? t('commun.reduire') : t('commun.lireLaSuite'),
            onClick: flip,
            onKeyDown: (event: KeyboardEvent<HTMLParagraphElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                flip();
              }
            },
          }
        : {})}
    >
      {/* La ligne est coupée sur ce texte, pas sur le paragraphe : la zone tactile
          agrandie du paragraphe ne laisse plus voir le haut de la ligne suivante. */}
      <span ref={text} className="page-header__description-text">{props.children}</span>
    </p>
  );
}

export function PageHeader(props: {
  title: string;
  /** Sur-titre : la rubrique ou le type de fiche (« Dossier client »). */
  kicker?: string;
  /** Ancien nom du sur-titre, gardé le temps de la migration des écrans. */
  context?: string;
  /** Une phrase qui dit à quoi sert l'écran. */
  description?: ReactNode;
  backTo?: string;
  backLabel?: string;
  /** État de la fiche, affiché à côté du titre. */
  badge?: ReactNode;
  /** L'action principale de l'écran : une seule. */
  primary?: Action;
  /** Une action secondaire, visible à côté de la principale. */
  secondary?: Action;
  /** Actions rares ou destructives, dans le menu « … ». */
  menu?: ActionMenuItem[];
  /** Bouton d'aide de la page (PageHelpButton). */
  help?: ReactNode;
  /** Emplacement libre de l'ancien en-tête, retiré écran par écran. */
  actions?: ReactNode;
  /** Onglets de la page, sous le titre. */
  tabs?: ReactNode;
  /** Liste au téléphone : phrase d'usage sur une ligne, dépliable. */
  compactDescription?: boolean;
  /**
   * L'action principale est reprise ailleurs au téléphone et sur tablette
   * (bouton collé au pouce d'une liste) : l'en-tête ne l'affiche que sur ordinateur.
   */
  primaryAtThumb?: boolean;
}): ReactNode {
  const location = useLocation();
  const stateFrom = (location.state as { from?: string } | null)?.from;
  const backTo = stateFrom ?? props.backTo;
  // La barre du haut reprend titre et retour quand l'en-tête passe dessous.
  const titleRow = useRef<HTMLDivElement>(null);
  usePageChrome({ title: props.title, backTo, backLabel: props.backLabel }, titleRow);
  const kicker = props.kicker ?? props.context;
  const hasActions = Boolean(props.primary || props.secondary || props.help || props.actions);
  // Le « … » reste à droite du titre au téléphone, même à 320 px : il ne tombe
  // jamais seul sur sa ligne sous des boutons trop larges pour le partager.
  const hasMenu = Boolean(props.menu?.length);
  return (
    <header className="page-header page-header--contextual" data-ui="page-header">
      {backTo ? (
        <Link className="page-header__back" to={backTo} aria-label={props.backLabel ?? 'Retour'}>
          <ArrowLeft size={19} aria-hidden="true" />
          <span>{props.backLabel ?? 'Retour'}</span>
        </Link>
      ) : null}
      <div className={hasMenu ? 'page-header__identity page-header__identity--menu' : 'page-header__identity'}>
        <div className="page-header__titles">
          {kicker ? <span className="page-header__kicker">{kicker}</span> : null}
          <div className="page-header__title-row" ref={titleRow}>
            <h1>{props.title}</h1>
            {props.badge}
          </div>
          {props.description ? (
            <PageDescription compact={Boolean(props.compactDescription)}>{props.description}</PageDescription>
          ) : null}
        </div>
        {hasActions ? (
          <div className="page-header__actions">
            {props.help}
            {props.actions}
            {props.secondary ? <ActionButton action={props.secondary} variant="secondary" /> : null}
            {props.primary ? (
              <ActionButton action={props.primary} {...(props.primaryAtThumb ? { className: 'page-header__primary-desktop' } : {})} />
            ) : null}
          </div>
        ) : null}
        {hasMenu ? (
          <div className="page-header__menu">
            <ActionMenu items={props.menu ?? []} />
          </div>
        ) : null}
      </div>
      {props.tabs ? <div className="page-header__tabs">{props.tabs}</div> : null}
    </header>
  );
}
